/**
 * Shaping layer for the Patient Notifications "by Discipline" card.
 *
 * `POST /dashboard/metric-discipline-notifications` returns the SP's document
 * unchanged — one row per (discipline × interval × MetricCode), four ETA metrics
 * per (discipline, date). Everything the card draws comes out of THIS payload:
 * the Trend panels plot the series, the Snapshot bars read its latest bucket.
 * No second request, no derived metric, no prior-period arithmetic.
 *
 * Kept out of the components so the card stays presentational and the reshaping
 * can be reasoned about (and reused) on its own.
 */

import { fmtNum } from "@/lib/utils/daylight-insight/format";
import type { DisciplineNotificationRow } from "./types";
import { unwrapRows } from "./unwrapRows";
import {
  ETA_ATTAINMENT_METRIC_CODE,
  ETA_OPTED_IN_METRIC_CODE,
  ETA_TEXTS_SENT_METRIC_CODE,
  ETA_VISITS_COMPLETED_METRIC_CODE,
} from "./useMetricDisciplineNotifications";

/** Attainment, as every label on the card shows it ("79.96%").
 *
 *  Runs through the dashboard's own {@link fmtNum} rather than rounding to one
 *  decimal: `toFixed(1)` printed the SP's 79.9614 as "80.0%", a number that was
 *  never in the payload and that disagreed with the same metric's KPI card and
 *  drill-down. The house rule TRUNCATES at two decimals, so every surface states
 *  the same figure. */
export const fmtPct = (v: number): string => `${fmtNum(v)}%`;

/** Counts — thousands-separated, under the same "as is" rule as every other
 *  number on the dashboard (whole values print whole, so the ETA counts read
 *  exactly as they did). */
export const fmtCount = (v: number | null): string =>
  v == null ? "—" : fmtNum(v);

/** ISO date → "6/28/2026". Falls back to the raw string when unparseable. */
export function fmtDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${Number(m[2])}/${Number(m[3])}/${m[1]}` : iso;
}

/** ISO date → "6/28" — the trend panels' first/last axis labels, where the year
 *  would not fit and every panel covers the same window anyway. */
export function fmtDateShort(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${Number(m[2])}/${Number(m[3])}` : iso;
}

/** The period a point covers — "7/5/2026-7/11/2026", or the single date when the
 *  interval is a day (start === end). */
export function fmtPeriod(start: string, end: string): string {
  const a = fmtDate(start);
  const b = end ? fmtDate(end) : "";
  return !b || b === a ? a : `${a}-${b}`;
}

/** The SP's own `SummaryInterval` ("Day"/"Week"/"Month") as the card's grain
 *  word. The grain therefore follows the page's date-range picker exactly the
 *  way the removed line chart's axis did — it is whatever bucketing the SP
 *  returned for the requested range. */
export function grainLabel(interval: string): string {
  switch (interval.trim().toLowerCase()) {
    case "day":
    case "daily":
      return "daily";
    case "week":
    case "weekly":
      return "weekly";
    case "month":
    case "monthly":
      return "monthly";
    default:
      return interval.trim().toLowerCase();
  }
}

/** Everything the hover card shows for one plotted point, beyond the attainment
 *  value itself: the period it covers and the three ETA counts behind it. */
export interface DisciplinePoint {
  /** "Day" / "Week" / "Month" — the SP's own SummaryInterval. */
  interval: string;
  period: string;
  sent: number | null;
  optedIn: number | null;
  visits: number | null;
}

export interface DisciplineSeries {
  discipline: string;
  /** One entry per date column; null where that discipline has no point. */
  values: (number | null)[];
  /** Same indexing as `values` — the hover card's supporting numbers. */
  points: (DisciplinePoint | null)[];
  /** Index of the last column this discipline actually has a value in, or -1. */
  latestIndex: number;
  /** Value at `latestIndex` — what the Snapshot bars plot. */
  latest: number | null;
}

/**
 * A numeric target for the plotted metric.
 *
 * The SP does NOT return one today. Nothing here hard-codes a value: the card
 * simply renders un-benchmarked (neutral bars, no marker, no reference line)
 * until one of the two sources below carries a number, at which point the
 * colouring, the bar marker, the panel reference line and the footnote all light
 * up with no further UI work:
 *   1. a benchmark column on the discipline-notification rows themselves, or
 *   2. the dashboard metric's `benchmarkLabel`, parsed by
 *      {@link parseBenchmarkLabel} and passed in by the caller.
 */
export interface DisciplineBenchmark {
  value: number;
  /** How the target reads — "≥80%". Synthesised from `value` when the payload
   *  carries no label of its own. */
  label: string;
  /** True when a LOWER value beats the target (drives sorting and colouring). */
  lowerBetter: boolean;
}

export interface DisciplineSeriesData {
  /** Period start dates, ISO, ascending — the shared x axis. */
  isoDates: string[];
  series: DisciplineSeries[];
  /** The SP's `SummaryInterval` for these rows ("Day" / "Week" / "Month"). */
  interval: string;
  /** Target carried by the payload, or null while the SP sends none. */
  benchmark: DisciplineBenchmark | null;
}

/** Which of the SP's four metrics a row carries. Matched case-insensitively,
 *  since MetricCode casing has differed between result sets elsewhere. */
const COUNT_FIELD: Record<string, "sent" | "optedIn" | "visits"> = {
  [ETA_TEXTS_SENT_METRIC_CODE.toLowerCase()]: "sent",
  [ETA_OPTED_IN_METRIC_CODE.toLowerCase()]: "optedIn",
  [ETA_VISITS_COMPLETED_METRIC_CODE.toLowerCase()]: "visits",
};

// ── Benchmark discovery ──────────────────────────────────────────────────────
// The column has no agreed name yet, so the row is probed for the spellings the
// warehouse uses elsewhere ("BenchMarkLabel", "TargetHigherIsBetter", …) rather
// than one guessed key. Comparison is on a normalised key, so spacing and
// underscores don't matter.
const normKey = (k: string): string => k.replace(/[\s_]/g, "").toLowerCase();
const BENCH_VALUE_KEYS = new Set([
  "benchmark",
  "benchmarkvalue",
  "benchvalue",
  "benchmarkmetricvalue",
  "metricbenchmark",
  "target",
  "targetvalue",
]);
const BENCH_LABEL_KEYS = new Set([
  "benchmarklabel",
  "benchlabel",
  "targetlabel",
]);
const HIGHER_BETTER_KEYS = new Set(["targethigherisbetter", "higherisbetter"]);
const LOWER_BETTER_KEYS = new Set([
  "targetlowerisbetter",
  "lowerisbetter",
  "lowerbetter",
]);

function pickKey(row: Record<string, unknown>, keys: Set<string>): unknown {
  for (const [k, v] of Object.entries(row)) {
    if (keys.has(normKey(k))) return v;
  }
  return undefined;
}

/** A flag column that may arrive as 1/0, true/false or "1"/"0". */
function asFlag(v: unknown): boolean | null {
  if (v === true || v === 1 || v === "1" || v === "true") return true;
  if (v === false || v === 0 || v === "0" || v === "false") return false;
  return null;
}

/** "≥80%" / "≤ 2.5 days" / "80" → the number in it. Null when the label carries
 *  no single number (e.g. "Top quartile"), so a descriptive target can never be
 *  mistaken for a numeric one. */
export function parseBenchmarkNumber(label: string): number | null {
  const nums = label.match(/-?\d+(?:\.\d+)?/g);
  if (!nums || nums.length !== 1) return null;
  const n = Number(nums[0]);
  return Number.isFinite(n) ? n : null;
}

/** Direction implied by a label's comparison sign, when it has one. */
function labelLowerBetter(label: string): boolean | null {
  if (/[≤<]/.test(label)) return true;
  if (/[≥>]/.test(label)) return false;
  return null;
}

const benchLabelFor = (value: number, lowerBetter: boolean): string =>
  `${lowerBetter ? "≤" : "≥"}${fmtPct(value)}`;

/**
 * Adapt a dashboard metric's benchmark LABEL into a numeric target — the second
 * source described on {@link DisciplineBenchmark}. `higherIsBetter` comes from
 * the same metric row; the label's own ≥/≤ wins when it disagrees with nothing.
 * Returns null for a label with no single number in it.
 */
export function parseBenchmarkLabel(
  label: string | null | undefined,
  higherIsBetter: boolean | null | undefined,
): DisciplineBenchmark | null {
  const text = (label ?? "").trim();
  if (!text) return null;
  const value = parseBenchmarkNumber(text);
  if (value == null) return null;
  const lowerBetter =
    labelLowerBetter(text) ?? (higherIsBetter === false ? true : false);
  return { value, label: text, lowerBetter };
}

/** The payload's own benchmark, if any row carries one. */
function benchmarkFromRows(
  rows: DisciplineNotificationRow[],
): DisciplineBenchmark | null {
  for (const r of rows) {
    const row = r as Record<string, unknown>;
    const raw = pickKey(row, BENCH_VALUE_KEYS);
    if (raw === null || raw === undefined || raw === "") continue;
    const value = Number(raw);
    if (!Number.isFinite(value)) continue;

    const rawLabel = pickKey(row, BENCH_LABEL_KEYS);
    const label = rawLabel == null ? "" : String(rawLabel).trim();
    const higher = asFlag(pickKey(row, HIGHER_BETTER_KEYS));
    const lower = asFlag(pickKey(row, LOWER_BETTER_KEYS));
    const lowerBetter =
      labelLowerBetter(label) ?? lower ?? (higher === false ? true : false);

    return { value, label: label || benchLabelFor(value, lowerBetter), lowerBetter };
  }
  return null;
}

const emptyPoint = (): DisciplinePoint => ({
  interval: "",
  period: "",
  sent: null,
  optedIn: null,
  visits: null,
});

/**
 * Reshape the raw notification rows into a shared date axis plus one series per
 * discipline. The payload is `{ DisciplineSummaryValues: [...] }` (a bare array
 * is tolerated too).
 *
 * The SP returns FOUR metrics per (discipline × date), so the rows are split by
 * `MetricCode`: only ETA_PercentEligibleTextsSent is plotted — the raw counts
 * are on a different scale entirely — and the other three ride along as that
 * point's hover details.
 *
 * Discipline codes carry trailing spaces ("HHA ") — trim them; align every
 * series to the same sorted date columns.
 */
export function toDisciplineSeries(data: unknown): DisciplineSeriesData {
  const rows = unwrapRows<DisciplineNotificationRow>(
    data,
    "DisciplineSummaryValues",
  );
  const attainmentCode = ETA_ATTAINMENT_METRIC_CODE.toLowerCase();

  const isoSet = new Set<string>();
  for (const r of rows) {
    const iso = String(r.DateSummaryStart ?? "");
    if (iso) isoSet.add(iso);
  }
  const isoDates = [...isoSet].sort();

  const byDisc = new Map<string, Map<string, number>>();
  const pointsByDisc = new Map<string, Map<string, DisciplinePoint>>();
  const order: string[] = [];
  let interval = "";

  const pointFor = (disc: string, iso: string): DisciplinePoint => {
    if (!pointsByDisc.has(disc)) pointsByDisc.set(disc, new Map());
    const forDate = pointsByDisc.get(disc)!;
    let point = forDate.get(iso);
    if (!point) {
      point = emptyPoint();
      forDate.set(iso, point);
    }
    return point;
  };

  for (const r of rows) {
    const disc = String(r.Discipline ?? "").trim();
    const iso = String(r.DateSummaryStart ?? "");
    const code = String(r.MetricCode ?? "").trim().toLowerCase();
    const val = Number(r.MetricValue);
    if (!disc || !iso || !Number.isFinite(val)) continue;

    // The period/interval labels live on every row of the group, so take them
    // from whichever arrives first rather than only from the attainment row.
    const point = pointFor(disc, iso);
    if (!point.period) {
      point.interval = String(r.SummaryInterval ?? "").trim();
      point.period = fmtPeriod(iso, String(r.DateSummaryEnd ?? ""));
    }
    if (!interval) interval = point.interval;

    if (code === attainmentCode) {
      if (!byDisc.has(disc)) {
        byDisc.set(disc, new Map());
        order.push(disc);
      }
      byDisc.get(disc)!.set(iso, val);
      continue;
    }

    const field = COUNT_FIELD[code];
    if (!field) continue; // a metric the card doesn't surface — ignore it
    point[field] = val;
  }

  const series: DisciplineSeries[] = order.map((disc) => {
    const values = isoDates.map((d) => byDisc.get(disc)?.get(d) ?? null);
    let latestIndex = -1;
    for (let i = values.length - 1; i >= 0; i--) {
      if (values[i] != null) {
        latestIndex = i;
        break;
      }
    }
    return {
      discipline: disc,
      values,
      points: isoDates.map((d) => pointsByDisc.get(disc)?.get(d) ?? null),
      latestIndex,
      latest: latestIndex < 0 ? null : values[latestIndex],
    };
  });

  return { isoDates, series, interval, benchmark: benchmarkFromRows(rows) };
}

// ── Benchmark-driven state ───────────────────────────────────────────────────

/** Does this value beat the target? Null when there is no target to judge it
 *  against (or no value), which is what keeps the card neutral today. */
export function beatsBenchmark(
  value: number | null,
  benchmark: DisciplineBenchmark | null,
): boolean | null {
  if (value == null || benchmark == null) return null;
  return benchmark.lowerBetter
    ? value <= benchmark.value
    : value >= benchmark.value;
}

/** Green when the value beats the target, red when it misses, the neutral chart
 *  colour when there is no target. The card's ONLY colour rule — no palette, no
 *  per-discipline hues, hence no legend. */
export function benchmarkColor(beats: boolean | null): string {
  if (beats == null) return "var(--chart-primary)";
  return beats ? "var(--green)" : "var(--red)";
}

/** `good` / `bad` / "" for the value labels, matching the shared status classes. */
export function benchmarkClass(beats: boolean | null): string {
  if (beats == null) return "";
  return beats ? "good" : "bad";
}

/** Best → worst on the latest bucket (ascending when a lower value is better —
 *  the direction is the metric's, so it holds with or without a benchmark).
 *  Disciplines with no value at all sort last. */
export function sortByLatest(
  series: DisciplineSeries[],
  lowerBetter: boolean,
): DisciplineSeries[] {
  return [...series].sort((a, b) => {
    if (a.latest == null || b.latest == null) {
      return a.latest == null ? (b.latest == null ? 0 : 1) : -1;
    }
    return lowerBetter ? a.latest - b.latest : b.latest - a.latest;
  });
}

/**
 * The Snapshot bars' x domain. The plotted metric is a percentage, so the track
 * runs 0–100 and the bars stay comparable with every other percentage on the
 * page; a payload that somehow exceeds 100 (or a target above it) widens the
 * domain rather than overflowing the track.
 */
export function barDomainMax(
  series: DisciplineSeries[],
  benchmark: DisciplineBenchmark | null,
): number {
  const peak = Math.max(
    0,
    ...series.map((s) => s.latest ?? 0),
    benchmark?.value ?? 0,
  );
  return peak > 100 ? peak * 1.05 : 100;
}

/**
 * The ONE y domain every trend panel shares: the full spread of every series,
 * padded two points either side. Deliberately NOT 0–100 — the whole point of
 * the small multiples is that a band of values sitting between ~72% and ~90%
 * becomes readable. The benchmark is folded in when there is one, so its
 * reference line can never fall outside the panel that must show it.
 */
export function trendDomain(
  series: DisciplineSeries[],
  benchmark: DisciplineBenchmark | null,
): [number, number] {
  const vals = series
    .flatMap((s) => s.values)
    .filter((v): v is number => v != null);
  if (!vals.length) return [0, 100];
  let lo = Math.min(...vals);
  let hi = Math.max(...vals);
  if (benchmark) {
    lo = Math.min(lo, benchmark.value);
    hi = Math.max(hi, benchmark.value);
  }
  lo -= 2;
  hi += 2;
  // A flat series would collapse the domain — keep a band to draw inside.
  return hi - lo < 1 ? [lo - 0.5, hi + 0.5] : [lo, hi];
}
